"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  number: "01",
  question: "How long does it take to see results from your SEO services?",
  answer: "SEO is a long-term investment in your digital presence. While technical fixes can show immediate improvements in site health, meaningful shifts in search rankings typically begin to appear within 3 to 6 months. We provide monthly performance reports to track your growth trajectory."
}, {
  number: "02",
  question: "Can you help if my Google Business Profile is suspended?",
  answer: "Yes, resolving Google Listing issues is one of our core specialties. We guide you through the reinstatement process, help gather the necessary documentation, and communicate with Google support to get your business back on the map as quickly as possible."
}, {
  number: "03",
  question: "Do you offer ongoing maintenance for website design?",
  answer: "We believe a website should grow with your business. We offer flexible maintenance packages that include security updates, performance monitoring, and content adjustments to ensure your site remains fast, secure, and relevant to your customers."
}, {
  number: "04",
  question: "What makes your local SEO strategy different?",
  answer: "We focus on hyper-local signals that matter most to your community. By optimizing your Google Business Profile, managing local citations, and building location-specific content, we ensure your business is the first choice for customers searching in your immediate area."
}, {
  number: "05",
  question: "Do you work with small businesses on a budget?",
  answer: "Absolutely. We specialize in helping small-to-medium businesses maximize their marketing ROI. We offer tiered service packages designed to provide high-impact results without the overhead of a large agency, ensuring your budget is spent where it counts."
}, {
  number: "06",
  question: "How do we get started with a project?",
  answer: "Getting started is simple. Reach out through our contact form to schedule a discovery call. We will discuss your current challenges, review your digital footprint, and outline a clear, actionable plan to help your business grow online."
}, {
  number: "07",
  question: "Do you provide custom website designs?",
  answer: "Every website we build is custom-tailored to your brand identity and business goals. We don't use cookie-cutter templates; instead, we design responsive, high-converting layouts that reflect your unique value proposition and engage your target audience."
}, {
  number: "08",
  question: "What information do you need from me?",
  answer: "To begin, we typically need access to your existing website, Google Business Profile, and any branding assets you have. During our initial consultation, we will provide a checklist of everything required to ensure a smooth and efficient onboarding process."
}];
export default function FrequentlyAskedQuestions() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const list = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3235" id="frequently-asked-questions" className="dark bg-background py-24 md:py-32 lg:py-[140px] px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-24 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-8">
            <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-foreground/70 mb-6">— FAQ / SEO SERVICES</p>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-foreground tracking-tight">
              Common questions,{" "}
              <span className="italic font-light">expertly</span> answered.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end lg:items-end">
            <Link to="/ContactUsPage" className="font-mono text-xs uppercase tracking-widest text-foreground hover:text-primary transition-colors duration-300 inline-flex items-center gap-2 border-b border-foreground/30 hover:border-foreground pb-1">
              Still have questions? Contact us →
            </Link>
          </div>
        </div>

        {}
        <div ref={list.ref} className={`max-w-[880px] mx-auto transition-all duration-700 ease-out delay-150 ${list.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="group border-b border-secondary/40 last:border-b-0 data-[state=open]:border-l-2 data-[state=open]:border-l-primary data-[state=open]:pl-6 transition-all duration-300">
                <AccordionTrigger className="flex items-start justify-between gap-6 py-7 md:py-8 hover:no-underline [&>svg]:hidden text-left">
                  <div className="flex items-start gap-6 md:gap-10 flex-1">
                    <span className="font-mono text-xs md:text-sm text-foreground/50 pt-2 shrink-0 tracking-wider">
                      {faq.number}
                    </span>
                    <span className="font-default font-semibold text-lg md:text-[22px] leading-snug text-primary">
                      {faq.question}
                    </span>
                  </div>
                  <Plus className="size-5 md:size-6 text-primary shrink-0 mt-2 transition-transform duration-400 ease-out group-data-[state=open]:rotate-45" strokeWidth={1} />
                </AccordionTrigger>
                <AccordionContent className="pl-12 md:pl-[72px] pr-10 md:pr-12 pb-8">
                  <p className="font-default text-base text-foreground/75 leading-[1.8]">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}